'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface Profile {
  id: string;
  email: string;
  username: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  website: string | null;
  location: string | null;
  role: 'enthusiast' | 'icon' | 'admin';
  is_active: boolean;
  is_onboarded: boolean;
  follower_count: number;
  following_count: number;
  post_count: number;
  created_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const supabase = createClient();

  const fetchProfile = async (userId: string) => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(data as Profile);
      }
    } catch {
      // profile fetch failed
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const username = metadata?.username || email.split('@')[0];
    const displayName = metadata?.displayName || username;
    const role = metadata?.role || 'enthusiast';

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName,
          avatar_url: metadata?.avatarUrl || '',
          role,
          username,
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;

    // Immediately upsert the profile row so it exists regardless of
    // email confirmation status or trigger timing.
    if (data?.user?.id) {
      // If signup returned a session, set it so the RLS-authenticated upsert works
      if (data.session) {
        await supabase.auth.setSession(data.session);
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(
          {
            id: data.user.id,
            email,
            username,
            display_name: displayName,
            avatar_url: metadata?.avatarUrl || null,
            role: role as 'enthusiast' | 'icon' | 'admin',
            is_onboarded: false,
          },
          { onConflict: 'id' }
        );

      if (profileError) {
        // Surface the error so the caller can show it to the user
        throw new Error(`Account created but profile setup failed: ${profileError.message}`);
      }

      // Set profile in context immediately so onboarding page can read it
      setProfile({
        id: data.user.id,
        email,
        username,
        display_name: displayName,
        bio: null,
        avatar_url: metadata?.avatarUrl || null,
        cover_url: null,
        website: null,
        location: null,
        role: role as 'enthusiast' | 'icon' | 'admin',
        is_active: true,
        is_onboarded: false,
        follower_count: 0,
        following_count: 0,
        post_count: 0,
        created_at: new Date().toISOString(),
      });
      setUser(data.user);
    }

    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  return (
    <AuthContext.Provider value={{
      user, session, profile, loading, profileLoading,
      signUp, signIn, signOut, refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
